import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { Alert, Button, Col, Container, ProgressBar, Row, Spinner } from "react-bootstrap";
import { FileUploader } from "react-drag-drop-files";
import { useTranslation } from "react-i18next";
import { FaCheckCircle, FaCloudUploadAlt, FaReceipt } from "react-icons/fa";
import { toast } from "react-toastify";
import "../css/ReceiptUploadPage.css";
import AppContext from "../context/app-context";
import userProfileService from "../services/userProfileService";
import { delay } from "../utils/helperFunctions";
import { useNavigate } from "react-router-dom";

const fileTypes = ["JPG", "PNG", "JPEG", "GIF"];

const ReceiptUploadPage = () => {
  const { t } = useTranslation(); // Use translation hook

  const [files, setFiles] = useState([]);
  const [fileProgress, setFileProgress] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const MAX_PER_DAY = 5;
  const [showFireworks, setShowFireworks] = useState(false);
  const navigate = useNavigate();
  const Fireworks = () => (
    <>
      <div className="fireworks-message">
        <div>🎉</div>
        {t("receiptUpload.successMessage")}
      </div>
      <div className="firework">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="firework-particle" />
        ))}
      </div>
    </>
  );
  const { userProfile, setLoading, loadItemsFromOnline, trackingItems = [] } = useContext(AppContext);

  useEffect(() => {
    if (!userProfile) {
      toast.info("Please login or signup first!");
      navigate("/login?redirect=/upload");
    }
  }, [userProfile]);
  const today = new Date();
  const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const itemsCreatedToday = trackingItems.filter((item) => {
    const createdAt = new Date(item.createdDate);
    return createdAt >= startOfDay;
  });

  const handleChange = (selectedFiles) => {
    selectedFiles = Object.values(selectedFiles);

    const existingFileNames = new Set(files.map((file) => file.name));
    const newValidFiles = [];
    const progressInit = {};

    selectedFiles.forEach((file) => {
      if (existingFileNames.has(file.name)) {
        toast.warn(`File "${file.name}" already exists. File names must be unique.`);
      } else {
        newValidFiles.push(file);
        progressInit[file.name] = { progress: 0, status: "pending" };
      }
    });

    if (newValidFiles.length > 0) {
      const updatedFiles = [...files, ...newValidFiles].slice(-5); // keep last 5
      const updatedProgress = { ...fileProgress, ...progressInit };
      const finalProgress = {};
      updatedFiles.forEach((file) => {
        finalProgress[file.name] = updatedProgress[file.name];
      });

      setFiles(updatedFiles);
      setFileProgress(finalProgress);
    }
  };

  const handleSubmit = async () => {
    if (!files.length) {
      setUploadMsg(t("receiptUpload.errorMessage"));
      return;
    }
    setLoading(true);
    setUploading(true);
    setUploadMsg("");

    try {
      const fileMetadata = files.map((file) => {
        const { name, type, size } = file;
        return { name, type, size };
      });

      userProfileService.createExpenseResources({
        attachments: fileMetadata,
        callback: async ({ data, error }) => {
          if (error) {
            toast.error(error);
            setUploading(false);
            setLoading(false);
            return;
          }

          const { presignUrls, expenseResource } = data;

          if (presignUrls.length !== files.length) {
            throw new Error("Mismatch between presigned URLs and files.");
          }

          // Upload each file with progress
          const uploadPromises = files.map((file, index) => {
            const url = presignUrls[index];

            return axios
              .put(url, file, {
                headers: {
                  "Content-Type": file.type,
                },
                onUploadProgress: (progressEvent) => {
                  const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                  setFileProgress((prev) => ({
                    ...prev,
                    [file.name]: { progress: percent, status: "uploading" },
                  }));
                },
              })
              .then(() => {
                setFileProgress((prev) => ({
                  ...prev,
                  [file.name]: { progress: 100, status: "success" },
                }));
              })
              .catch(() => {
                setFileProgress((prev) => ({
                  ...prev,
                  [file.name]: { progress: 0, status: "error" },
                }));
              });
          });
          await Promise.all(uploadPromises);
          setShowFireworks(true);
          setTimeout(() => setShowFireworks(false), 5000);
          setLoading(false);
          await delay(5);
          loadItemsFromOnline();
          setUploadMsg(t("receiptUpload.uploadComplete"));
          setFiles([]); // Clear files
          setTimeout(() => {
            loadItemsFromOnline && loadItemsFromOnline();
          }, 5000);
        },
      });
    } catch (error) {
      console.error("Upload error:", error);
      setUploadMsg(t("receiptUpload.errorMessage"));
    } finally {
      setUploading(false);
    }
  };

  const uploadZoneStyle = {
    border: "3px dashed #6EC3C4",
    borderRadius: "20px",
    padding: "2rem",
    backgroundColor: "#f8f9fa",
    cursor: "pointer",
    transition: "all 0.3s ease",
    ":hover": {
      backgroundColor: "#f0faff",
      borderColor: "#FF7F7F",
    },
  };

  const progressStyle = {
    height: "15px",
    borderRadius: "10px",
    backgroundColor: "#e9ecef",
    overflow: "hidden",
  };
  const disableUploadBtn = uploading || files.length === 0 || itemsCreatedToday.length >= MAX_PER_DAY;

  return (
    <Container className="mt-5 py-4">
      <Row className="justify-content-md-center">
        <Col xs={12} md={8}>
          <div className="text-center mb-4">
            <h1 className="display-5 mb-3">{t("receiptUpload.title")}</h1>
            <p className="lead text-muted">{t("receiptUpload.instructions")}</p>
          </div>
          <div className="text-center mb-4">
            <FileUploader
              multiple={true}
              handleChange={handleChange}
              name="file"
              types={fileTypes}
              style={{ width: "100%" }}
            >
              <div style={uploadZoneStyle} className="text-center">
                <FaCloudUploadAlt size="3em" className="text-primary mb-3" />
                <h4 className="text-secondary">{t("receiptUpload.dropAreaText")}</h4>
                <p className="text-muted mb-0">{t("receiptUpload.fileFormats")}</p>
                <div className="mt-2">
                  <img src="/kola2.jpg" alt="Scanning hamster" style={{ width: "120px", opacity: 0.8 }} />
                </div>
              </div>
            </FileUploader>
          </div>
          <div className="mt-4">
            {files.length > 0 && (
              <>
                <h4 className="mb-3">{t("receiptUpload.selectedReceipts")}</h4>
                <div className="file-list">
                  {files.map((file, idx) => {
                    const progressData = fileProgress[file.name] || { progress: 0, status: "pending" };
                    return (
                      <div key={idx} className="mb-3 p-3 rounded-3 shadow-sm" style={{ backgroundColor: "white" }}>
                        <div className="d-flex align-items-center">
                          <FaReceipt className="text-primary me-3" size="1.5em" />
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between mb-2">
                              <strong className="text-truncate">{file.name}</strong>
                              <span className="text-muted" style={{ fontSize: "0.9em" }}>
                                {Math.round(file.size / 1024)}KB
                              </span>
                            </div>

                            {progressData.status !== "pending" && (
                              <ProgressBar
                                now={progressData.progress}
                                label={`${progressData.progress}%`}
                                variant={
                                  progressData.status === "error"
                                    ? "danger"
                                    : progressData.status === "success"
                                    ? "success"
                                    : "primary"
                                }
                                style={progressStyle}
                                animated={progressData.status === "uploading"}
                              />
                            )}
                          </div>
                        </div>

                        {progressData.status === "success" && (
                          <div className="text-end mt-2">
                            <FaCheckCircle className="text-success me-2" />
                            <span className="text-muted">{t("receiptUpload.uploadedSuccess")}</span>
                          </div>
                        )}

                        {progressData.status === "error" && (
                          <Alert variant="danger" className="mt-2 p-2 rounded">
                            ❌ {t("receiptUpload.uploadError")}
                          </Alert>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div className="text-center mt-4">
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={disableUploadBtn}
              size="lg"
              style={{
                minWidth: "200px",
                background: disableUploadBtn ? "gray" : "linear-gradient(135deg, #6EC3C4 0%, #FF7F7F 100%)",
                border: "none",
                borderRadius: "15px",
                padding: "1rem 2rem",
              }}
            >
              {uploading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  {t("receiptUpload.uploading")}
                </>
              ) : (
                <>
                  <FaCloudUploadAlt className="me-2" />
                  {t("receiptUpload.startMagicScan")}
                </>
              )}
            </Button>
          </div>
        </Col>
      </Row>
      {showFireworks && <Fireworks />}
    </Container>
  );
};

export default ReceiptUploadPage;
